import sanitizeHtml from 'sanitize-html'

import { type RoItem } from '@rewardops/api-utils'
import { type ItemVariant } from '@rewardops/api-utils/dist/types/models/items/item-variant.model'

import { formatNumberAsNumber, type ResponseItem } from '..'
import {
  MemberSpend,
  type TransformedItem,
  type Variant,
  type VariantList,
} from './types'

export type ItemPricing = {
  salePrice: undefined | number
  price: number
  regularPrice: undefined | number
  isOnSale: undefined | 0 | boolean
  retailPrice: undefined | number
}

export interface Tax {
  label: string
  value: number
}

export interface TaxCalculation {
  tax_name: string
  tax_value: number
  tax_currency: string
}

export interface TaxCalculationInput {
  amount: number
  currency: string
  taxes: Tax[]
  round?: boolean
}

export interface CustomLabel {
  name: string
  type: string
  level: string
  key: string
}

export function reverseCalculateBaseAmount(input: TaxCalculationInput): number {
  const totalTaxRate = input.taxes.reduce((acc, tax) => acc + tax.value, 0)
  const baseAmount = input.amount / (1 + totalTaxRate)
  return formatNumberAsNumber(baseAmount)
}

// TODO: Remove this function
// Calculate taxes based on the price of the item (eg. backwards from the price)
export function calculateTaxesFromPrice({
  amount,
  currency,
  taxes,
  round = true,
}: TaxCalculationInput): TaxCalculation[] {
  return taxes.map((tax) => ({
    tax_name: tax.label.split(' (')[0] ?? '', // Assuming the label format is "TAX_NAME (RATE%)"
    tax_value: round
      ? formatNumberAsNumber(amount * tax.value)
      : amount * tax.value,
    tax_currency: currency,
  }))
}

const getVariants = (
  item: RoItem,
  variantType: string,
): ItemVariant | undefined =>
  item.itemDetail.variantProperties.find(
    (variantProperty) => variantProperty.summary.key === variantType,
  )

const cleanNumber = (number?: string) => {
  if (!number) return undefined
  return parseFloat(number.replace(/[^0-9.-]+/g, ''))
}

export const getItemPricing = (
  item: ResponseItem,
  currency: string,
): ItemPricing => {
  const regularPrice = cleanNumber(
    item.supplier_items?.[0]?.prices.regular_price[currency] ??
      item.supplier_items[0]?.retail_price[currency],
  )
  const retailPrice = cleanNumber(
    item.supplier_items?.[0]?.prices.retail_price[currency],
  )
  const salePrice = cleanNumber(
    item.supplier_items?.[0]?.prices.sale_price[currency],
  )

  const isOnSale = salePrice && regularPrice && salePrice < regularPrice
  const price = isOnSale ? salePrice : regularPrice

  return {
    price: price || 99999999, // Set to a high number if no price is found
    regularPrice,
    retailPrice,
    salePrice,
    isOnSale,
  }
}

const getItemVariants = (item: RoItem): number[] => {
  const colorVariants = getVariants(item, 'color')
  const otherVariants =
    colorVariants && colorVariants.otherVariants
      ? colorVariants.otherVariants.filter((variant) => variant.variantId)
      : []

  const variantIds = otherVariants
    ? [...otherVariants.map((variant) => variant.variantId), item.itemDetail.id]
    : [item.itemDetail.id]

  return variantIds
}

const getItemPrice = (roItem: RoItem): number =>
  roItem.itemDetail.pricing?.isOnSale
    ? roItem.itemDetail.pricing.salePrice
    : roItem.itemDetail.pricing.regularPrice

const isSplitPayEnabled = (item: RoItem): boolean => {
  const { customProperties } = item?.itemDetail || {}

  if (!customProperties || customProperties?.length === 0) return false

  const splitPayProperty = customProperties.find(
    (property) => property.summary.key === 'split_payment',
  )

  return Boolean(splitPayProperty && splitPayProperty?.summary?.value == 'true')
}

export const getItemCustomLabel = (item: ResponseItem): CustomLabel | null => {
  if (!item) return null

  if (!item?.supplier_items?.[0]) return null

  const { static_labels = [], promotion_labels = [] } = item.supplier_items[0]

  // Pangea responds an array, but it should display only one label
  // If it responds more then it's admin error
  const label =
    static_labels.length > 0 ? static_labels[0] : promotion_labels[0]

  if (!label) return null
  // PromotionLabel does not have 'type', CustomLabelData does
  return {
    name: label.name,
    type: 'type' in label && label.type ? label.type : '',
    level: label.level as string,
    key: label.key,
  }
}

export const getTransformedItem = (roItem: RoItem): TransformedItem => {
  const variantTypes = roItem.itemDetail.variantProperties.map(
    (variant) => variant.summary.key,
  )

  const variantIds = getItemVariants(roItem)
  const variantOptions: {
    [key: string]: number
  } = {}
  variantIds.forEach((variant) => {
    variantOptions[variant] = 0
  })

  let variants: VariantList[] = []

  // First pass to collect total data
  for (const type of variantTypes) {
    const variant = getVariants(roItem, type)
    if (!variant) continue

    const { value } = variant.summary

    variant.otherVariants.forEach((variant) => {
      if (value !== variant.value) {
        if (typeof variantOptions[variant.variantId] !== 'number') {
          variantOptions[variant.variantId] = 0
        }
        variantOptions[variant.variantId]! += 1
      }
    })
  }

  for (const type of variantTypes) {
    const variant = getVariants(roItem, type)
    if (!variant) continue

    const { value, key, name } = variant.summary
    const bestOptions: {
      [key: string]: Variant
    } = {}
    variant.otherVariants
      .filter((variant) => value !== variant.value)
      .forEach((variant) => {
        if (
          !bestOptions[variant.value] ||
          (variantOptions[variant.variantId] || -1) <
            (bestOptions[variant.value]?.differences || -1)
        ) {
          bestOptions[variant.value] = {
            value: variant.value,
            variantId: variant.variantId,
            differences: variantOptions[variant.variantId] ?? 0,
          }
        }
      })

    const options: Variant[] = Object.values(bestOptions)

    variants.push({
      value,
      options,
      type: key,
      label: name,
    })
  }

  variants = variants.sort((a, b) => {
    return a.type < b.type ? -1 : 1
  })

  const price = getItemPrice(roItem)
  const isGiftCard = roItem.itemDetail.type === 'giftcard'
  const isSplitPay = isSplitPayEnabled(roItem)

  return {
    id: roItem.itemDetail.id,
    sivId: roItem.itemDetail.supplierItemId,
    orderToken: roItem.itemDetail.orderToken,
    price,
    variantIds,
    variants,
    name: roItem.itemDetail.name,
    model: roItem.itemDetail.model,
    brands: roItem.itemDetail.brands,
    status: roItem.itemDetail.status,
    description: sanitizeHtml(roItem.itemDetail.description),
    termsAndConditions: sanitizeHtml(roItem.itemDetail.fulfillmentInstructions),
    specifications: sanitizeHtml(roItem.itemDetail.specifications),
    images: roItem.itemDetail.images.map((image) => image.url),
    imagesDetailed: roItem.itemDetail.images,
    categories: roItem.itemDetail.categories,
    isGiftCard,
    isSplitPayEnabled: isSplitPay,
    // Add required TransformedItem fields with default/empty values
    categoryDetails: {} as any,
    customLabel: {} as any,
  } as TransformedItem
}

// Convert amount to cents before performing any division
export function convertToCents(amount: number) {
  return Math.round(amount * 100)
}

// Helper function to calculate per unit tax and round it, converting back to dollars
export function getPerUnitTaxAmount(tax: TaxCalculation, quantity: number) {
  const totalTaxInCents = convertToCents(tax.tax_value)
  const perUnitTaxCents = Math.floor(totalTaxInCents / quantity)
  return {
    tax_name: tax.tax_name,
    tax_value: perUnitTaxCents / 100,
    tax_currency: tax.tax_currency,
  }
}

// Adjust tax rounding errors on the last item
export function adjustTaxRoundingErrors(
  taxes: TaxCalculation[],
  distribution: MemberSpend[],
  quantity: number,
) {
  taxes.forEach((tax, index) => {
    const totalTaxInCents = convertToCents(tax.tax_value)
    const distributedTaxSum = distribution.reduce(
      (acc, curr) =>
        acc + convertToCents(curr.tax_split?.[index]?.tax_value ?? 0),
      0,
    )
    const diff = totalTaxInCents - distributedTaxSum // Difference in cents
    const distributionItemTaxSplit =
      distribution[quantity - 1]?.tax_split?.[index]

    if (diff !== 0 && distributionItemTaxSplit) {
      distributionItemTaxSplit.tax_value += diff / 100
    }
  })
}

export function adjustEnvironmentalTaxRoundingErrors(
  taxes: TaxCalculation[],
  distribution: MemberSpend[],
  quantity: number,
) {
  taxes.forEach((tax, index) => {
    const totalTaxInCents = convertToCents(tax.tax_value)
    const distributedTaxSum = distribution.reduce(
      (acc, curr) =>
        acc +
        convertToCents(curr.environment_tax_split?.[index]?.tax_value ?? 0),
      0,
    )
    const diff = totalTaxInCents - distributedTaxSum // Difference in cents
    const distributionItemTaxSplit =
      distribution[quantity - 1]?.environment_tax_split?.[index]
    if (diff !== 0 && distributionItemTaxSplit) {
      distributionItemTaxSplit.tax_value += diff / 100
    }
  })
}

// Adjust the main amount rounding errors on the last item
export function adjustRoundingErrors(
  total: number,
  distribution: MemberSpend[],
  quantity: number,
) {
  const totalInCents = convertToCents(total)
  const distributedSum = distribution.reduce(
    (acc, curr) => acc + convertToCents(curr.amount ?? 0),
    0,
  )
  const diff = totalInCents - distributedSum
  const distributionItemTaxSplit = distribution[quantity - 1]

  if (diff !== 0 && distributionItemTaxSplit?.amount) {
    distributionItemTaxSplit.amount += diff / 100 // Adjust the last item in dollars
  }
}

interface TaxTotal {
  [taxName: string]: number
}

function calculateTaxTotals(items: any[]): TaxTotal {
  const totals: TaxTotal = {}

  items.forEach((item) => {
    item.member_spend.forEach((spend: any) => {
      if (!spend.tax_split) {
        return
      }
      spend.tax_split.forEach((tax: any) => {
        if (totals[tax.tax_name]) {
          totals[tax.tax_name] += tax.tax_value
        } else {
          totals[tax.tax_name] = tax.tax_value
        }
      })
    })
  })

  return totals
}

function calculateEhfTotals(items: any[]): TaxTotal {
  const totals: TaxTotal = {}

  items.forEach((item) => {
    item.member_spend.forEach((spend: any) => {
      if (!spend.environment_tax_split) {
        return
      }
      spend.environment_tax_split.forEach((tax: any) => {
        if (totals[tax.tax_name]) {
          totals[tax.tax_name] += tax.tax_value
        } else {
          totals[tax.tax_name] = tax.tax_value
        }
      })
    })
  })

  return totals
}

export function adjustTaxTotals(items: any[], expectedTotals: TaxTotal) {
  const calculatedTotals = calculateTaxTotals(items)

  Object.keys(expectedTotals).forEach((taxName) => {
    const expectedTotal = expectedTotals[taxName]

    if (!expectedTotal) {
      return
    }

    const calculatedTotal = calculatedTotals[taxName] || 0
    let diff = expectedTotal - calculatedTotal

    if (diff === 0) {
      return
    }

    // Spread the difference across all items or adjust on the last item
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i]
      const spend = item.member_spend.find((s: any) => {
        return s.tax_split
          ? s.tax_split.some((t: any) => t.tax_name === taxName)
          : false
      })
      const tax = spend?.tax_split.find((t: any) => t.tax_name === taxName)
      if (tax) {
        // Ensure tax_value is rounded to 2 decimal places
        tax.tax_value = formatNumberAsNumber(tax.tax_value + diff)
        break
      }
    }
  })
}

export function adjustEhfTotals(items: any[], expectedTotals: TaxTotal) {
  const calculatedEhfTotals = calculateEhfTotals(items)

  Object.keys(expectedTotals).forEach((taxName) => {
    const expectedTotal = expectedTotals[taxName]

    if (!expectedTotal) {
      return
    }

    const calculatedTotal = calculatedEhfTotals[taxName] || 0
    let diff = expectedTotal - calculatedTotal

    if (diff === 0) {
      return
    }

    // Spread the difference across all items or adjust on the last item
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i] as any
      const spend = item.member_spend.find((s: any) => {
        return s.environment_tax_split
          ? s.environment_tax_split.some((t: any) => t.tax_name === taxName)
          : false
      })
      const tax = spend?.environment_tax_split.find(
        (t: any) => t.tax_name === taxName,
      )
      if (tax) {
        // Ensure tax_value is rounded to 2 decimal places
        tax.tax_value = formatNumberAsNumber(tax.tax_value + diff)
        break
      }
    }
  })
}
